import { flyToCart, loadSwiper, observeOnce, popCart, prefersReducedMotion } from '@kame/lib';

function findVariant(variants, selected) {
  return variants.find((variant) => variant.options.every((value, index) => value === selected[index])) || null;
}

class KameProductMain extends HTMLElement {
  connectedCallback() {
    this.reduced = prefersReducedMotion();
    if (!this.reduced) {
      this.classList.add('is-armed');
      this.reveal = observeOnce(this, () => this.classList.add('is-inview'));
    }

    this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    this.form = this.querySelector('form[action*="/cart/add"]');
    this.idInput = this.form?.querySelector('[name="id"]');
    this.currentVariant = this.variants.find((variant) => String(variant.id) === this.idInput?.value) || null;
    this.pending = false;
    this.addButton = this.querySelector('[data-add]');
    this.viewer = this.querySelector('.kame-pm__viewer');

    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onVideoKey);
    this.form?.addEventListener('submit', this.onSubmit);
    this.viewer?.addEventListener('keydown', this.onViewerKey);

    this.initGallery();
    this.initVideos();
    this.initGlider();
  }

  disconnectedCallback() {
    this.reveal?.disconnect();
    this.gliderObserver?.disconnect();
    this.videoObserver?.disconnect();
    this.swiper?.destroy(true, true);
    this.swiper = null;
  }

  initGlider() {
    this.cards = this.querySelector('[data-cards]');
    this.glider = this.cards?.querySelector('[data-glider]');
    if (!this.glider) return;
    this.cards.classList.add('has-glider');
    this.moveGlider(false);
    if ('ResizeObserver' in window) {
      this.gliderObserver = new ResizeObserver(() => this.moveGlider(false));
      this.gliderObserver.observe(this.cards);
    }
  }

  moveGlider(animate = true) {
    if (!this.glider) return;
    const card = this.cards.querySelector('input:checked')?.closest('label');
    if (!card) {
      this.glider.classList.remove('is-on');
      return;
    }
    const first = !this.glider.classList.contains('is-on');
    if (first || !animate || this.reduced) this.glider.classList.add('is-instant');
    this.glider.style.setProperty('--kame-glider-y', `${card.offsetTop}px`);
    this.glider.style.setProperty('--kame-glider-x', `${card.offsetLeft}px`);
    this.glider.style.setProperty('--kame-glider-w', `${card.offsetWidth}px`);
    this.glider.style.setProperty('--kame-glider-h', `${card.offsetHeight}px`);
    void this.glider.offsetWidth;
    this.glider.classList.remove('is-instant');
    this.glider.classList.add('is-on');
  }

  async initGallery() {
    const container = this.querySelector('.kame-pm__swiper');
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    const slides = [...this.querySelectorAll('.kame-pm__slide')];
    const selectedIndex = () => Math.max(0, slides.findIndex((slide) => slide.dataset.mediaId === String(this.currentVariant?.media)));
    this.syncThumbs(selectedIndex());
    if (!container || container.querySelectorAll('.swiper-slide').length < 2) return;
    const Swiper = await loadSwiper(this.dataset.swiperSrc);
    if (!this.isConnected || this.swiper) return;

    const desktop = window.matchMedia('(min-width: 750px)').matches;
    this.swiper = new Swiper(container, {
      initialSlide: selectedIndex(),
      effect: desktop ? 'fade' : 'slide',
      fadeEffect: { crossFade: true },
      slidesPerView: desktop ? 1 : 1.08,
      spaceBetween: 8,
      speed: this.reduced ? 0 : 420,
      noSwipingSelector: 'video, model-viewer',
      navigation: { prevEl: this.querySelector('[data-prev]'), nextEl: this.querySelector('[data-next]') },
      pagination: { el: this.querySelector('.kame-pm__dots'), clickable: true },
      on: {
        init: (swiper) => this.syncThumbs(swiper.activeIndex),
        slideChange: (swiper) => this.syncThumbs(swiper.activeIndex),
      },
    });
  }

  syncThumbs(index) {
    this.querySelectorAll('.kame-pm__slide video').forEach((video) => video.pause());
    this.querySelectorAll('.kame-pm__slide').forEach((slide, i) => {
      slide.inert = i !== index;
      if (i === index) return;
      slide.querySelectorAll('iframe').forEach((iframe) => {
        if (iframe.src.includes('youtube.com') || iframe.src.includes('youtube-nocookie.com')) {
          iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*');
        } else if (iframe.src.includes('vimeo.com')) {
          iframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
        }
      });
    });
    this.thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    const thumb = this.thumbs[index];
    const list = thumb?.parentElement;
    if (!thumb || !list) return;
    list.scrollTo({
      top: thumb.offsetTop - (list.clientHeight - thumb.offsetHeight) / 2,
      left: thumb.offsetLeft - (list.clientWidth - thumb.offsetWidth) / 2,
      behavior: this.reduced ? 'auto' : 'smooth',
    });
  }

  initVideos() {
    const videos = this.querySelectorAll('.kame-pm__vid-media');
    if (!videos.length || this.reduced) return;
    this.videoObserver = new IntersectionObserver(
      (entries) =>
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting) target.play().catch(() => {});
          else target.pause();
        }),
      { threshold: 0.4 }
    );
    videos.forEach((video) => {
      video.muted = true;
      this.videoObserver.observe(video);
    });
  }

  toggleVideo(video) {
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  }

  onVideoKey = (event) => {
    if (!event.target.matches('.kame-pm__vid-media') || !['Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    if (!event.repeat) this.toggleVideo(event.target);
  };

  onClick = (event) => {
    if (event.target.matches('.kame-pm__vid-media')) {
      this.toggleVideo(event.target);
      return;
    }
    const target = event.target.closest(
      '[data-thumb], [data-zoom], [data-qty], [data-vids], [data-viewer-close], [data-viewer-step]'
    );
    if (!target) return;
    const { thumb, zoom, qty, vids, viewerStep } = target.dataset;

    if (thumb !== undefined) this.swiper?.slideTo(Number(thumb));
    else if (zoom !== undefined) this.openViewer(Number(zoom));
    else if (qty !== undefined) this.stepQuantity(Number(qty));
    else if (vids !== undefined) this.scrollTrack(this.querySelector('[data-vids-track]'), Number(vids));
    else if (viewerStep !== undefined) this.scrollTrack(this.viewerTrack(), Number(viewerStep));
    else this.viewer?.close();
  };

  stepQuantity(delta) {
    const input = this.querySelector('[name="quantity"]');
    if (!input) return;
    input.value = String(Math.max(1, (parseInt(input.value, 10) || 1) + delta));
  }

  scrollTrack(track, direction) {
    if (!track) return;
    const item = track.firstElementChild;
    const step = track === this.viewerTrack() ? track.clientWidth : (item?.offsetWidth || 0) + 8;
    track.scrollBy({ left: direction * step, behavior: this.reduced ? 'auto' : 'smooth' });
  }

  viewerTrack() {
    return this.viewer?.querySelector('[data-viewer-track]');
  }

  openViewer(index) {
    if (!this.viewer) return;
    this.viewer.showModal();
    const track = this.viewerTrack();
    const item = track?.children[index];
    if (item) track.scrollTo({ left: item.offsetLeft, behavior: 'auto' });
  }

  onViewerKey = (event) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.scrollTrack(this.viewerTrack(), event.key === 'ArrowLeft' ? -1 : 1);
    }
  };

  onChange = (event) => {
    if (!event.target.closest('[data-option]')) return;
    const fieldsets = [...this.querySelectorAll('[data-option]')];
    const selected = fieldsets.map((fieldset) => fieldset.querySelector('input:checked')?.value);

    fieldsets.forEach((fieldset, index) => {
      const label = fieldset.querySelector('[data-option-value]');
      if (label) label.textContent = selected[index];
      fieldset.querySelectorAll('input').forEach((input) => {
        const option = selected.map((value, i) => (i === index ? input.value : value));
        const match = findVariant(this.variants, option);
        const card = input.closest('label');
        card.toggleAttribute('data-unavailable', !match?.available);
        const price = card.querySelector('[data-card-price]');
        if (price) price.textContent = match?.price || '';
        const was = card.querySelector('[data-card-was]');
        if (was) {
          was.textContent = match?.compare || '';
          was.hidden = !match?.compare;
        }
      });
    });

    this.render(findVariant(this.variants, selected));
    this.moveGlider(true);
  };

  render(variant) {
    this.currentVariant = variant;
    if (this.idInput) this.idInput.value = variant?.id || '';
    const { labelAdd, labelSoldOut, labelUnavailable } = this.dataset;
    if (this.addButton) {
      this.addButton.disabled = this.pending || !variant?.available;
      const label = this.addButton.querySelector('[data-add-label]') || this.addButton;
      label.textContent = !variant ? labelUnavailable : variant.available ? labelAdd : labelSoldOut;
    }
    const dynamic = this.querySelector('[data-dynamic]');
    if (dynamic) dynamic.hidden = !variant?.available;
    if (!variant) return;

    this.querySelector('[data-price-now]').textContent = variant.price;
    const was = this.querySelector('[data-price-was]');
    was.hidden = !variant.compare;
    was.textContent = variant.compare || '';

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState(window.history.state, '', url);

    if (variant.media) {
      const slides = [...this.querySelectorAll('.kame-pm__slide')];
      const index = slides.findIndex((slide) => slide.dataset.mediaId === String(variant.media));
      if (index >= 0) this.swiper?.slideTo(index);
    }
  }

  onSubmit = async (event) => {
    if (this.pending || !this.currentVariant?.available || !this.idInput?.value) {
      event.preventDefault();
      return;
    }
    if (!window.fetch) return;
    event.preventDefault();
    const error = this.querySelector('[data-error]');
    const root = window.Shopify?.routes?.root || '/';
    const data = new FormData(this.form);
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);
    if (sections.length) data.set('sections', sections.join(','));

    this.pending = true;
    this.addButton.disabled = true;
    this.addButton.setAttribute('aria-busy', 'true');
    error.hidden = true;

    try {
      const minimum = new Promise((resolve) => setTimeout(resolve, this.reduced ? 0 : 450));
      const [response] = await Promise.all([
        fetch(`${root}cart/add.js`, {
          method: 'POST',
          body: data,
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        }),
        minimum,
      ]);
      const added = await response.json();
      if (!response.ok || added.status) {
        error.textContent = added.description || added.message || this.dataset.labelError;
        error.hidden = false;
        return;
      }
      await this.announce(added, data, root);
    } catch {
      window.location.href = `${root}cart`;
    } finally {
      this.pending = false;
      this.addButton.removeAttribute('aria-busy');
      this.addButton.disabled = !this.currentVariant?.available;
    }
  };

  async announce(added, data, root) {
    const drawer = document.querySelector('cart-drawer-component');
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    const [cart] = await Promise.all([
      fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()),
      flyToCart(this.addButton),
    ]);
    const quantity = Number(data.get('quantity')) || 1;
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(data.get('id')), quantity }],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'kame-product-main',
        sourceId: this.id,
        itemCount: quantity,
        productId: this.dataset.productId,
        sections: added.sections,
        didError: false,
      },
    });

    popCart();

    if (!drawer) {
      window.location.href = `${root}cart`;
      return;
    }
    const themeDrawer = drawer.closest('theme-drawer');
    if (!drawer.hasAttribute('auto-open') && themeDrawer && !themeDrawer.isOpen) themeDrawer.open();
  }
}

if (!customElements.get('kame-product-main')) customElements.define('kame-product-main', KameProductMain);
